import api from "@/api/api";
import { toast } from "@/components/utils/ui_v4/alerts/toast/toast";
import alturaDaJanela from "@/helpers/ResponsiveHeightReativo";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { formatCNPJ } from "../utils/stringUtils";
import useTableScroll from "./useTableScroll";

export default function useVisualizarFornecedoresCotacaoDialog(props) {
  const isLoading = ref(false);
  const isLoadingMore = ref(false);
  const fornecedores = ref([]);
  const selected = ref([]);
  const offset = ref(0);
  const limitInicial = 40;
  const limit = ref(limitInicial);
  const hasMore = ref(true);
  const cotacaoTitulo = ref('');
  const searchQuery = ref('');
  const filtros = ref({});
  let searchTimeout = null;
  const isLimpandoLista = ref(false);

  const alturaTabela = computed(() => alturaDaJanela.value - 350);

  // ============ CONTACT DIALOG MANAGEMENT ============
  const contatosDialogVisible = ref(false);
  const fornecedoresParaContatos = ref([]);
  const indiceFornecedorContato = ref(0);
  const mostrarFiltroSemContato = ref(false);

  const headers = [
    { text: "Fornecedor", value: "fornecedor", sortable: true },
    { text: "CNPJ", value: "cnpj", sortable: true },
    { text: "Cód. Fornecedor", value: "cod_fornecedor", sortable: true },
    { text: "Razão social", value: "razao_social", sortable: true },
    { text: "Nome fantasia", value: "nome_fantasia", sortable: true },
    { text: "Contatos", value: "contatos", sortable: false },
    {
      text: "Editar",
      value: "acoes",
      style: {
        backgroundColor: 'white',
        position: 'sticky !important',
        zIndex: '1 !important',
        minWidth: '80px !important',
        maxWidth: '80px !important',
        right: 0,
        left: 'auto',
        borderLeft: '2px solid rgba(0,0,0,.1)',
        cursor: 'pointer',
      }
    }
  ];

  const fornecedoresFiltrados = computed(() => {
    if (!searchQuery.value || searchQuery.value.trim() === '') {
      return fornecedores.value;
    }

    const query = searchQuery.value.toLowerCase().trim();
    const queryNumbers = query.replace(/\D/g, '');

    return fornecedores.value.filter((fornecedor) => {
      const cnpj = (fornecedor.cnpj || '').replace(/\D/g, '').toLowerCase();
      const cnpjMatch = cnpj.includes(queryNumbers);

      const razaoSocial = (fornecedor.razao_social || '').toLowerCase();
      const razaoSocialMatch = razaoSocial.includes(query);

      const nomeFantasia = (fornecedor.nome_fantasia || '').toLowerCase();
      const nomeFantasiaMatch = nomeFantasia.includes(query);

      return cnpjMatch || razaoSocialMatch || nomeFantasiaMatch;
    });
  });

  const getFornecedores = async (reset = false) => {
    if (!props.idCotacao) {
      toast.error("ID da cotação não informado.");
      return;
    }

    if (reset) {
      offset.value = 0;
      limit.value = limitInicial;
      fornecedores.value = [];
      hasMore.value = true;
    }

    if (reset) {
      isLoading.value = true;
      isLoadingMore.value = false;
    } else {
      isLoadingMore.value = true;
    }

    try {
      const currentLimit = offset.value === 0 ? limitInicial : offset.value + limitInicial;
      const params = new URLSearchParams();
      params.append('offset', offset.value.toString());
      params.append('limit', currentLimit.toString());

      Object.keys(filtros.value).forEach((key) => {
        const value = filtros.value[key];
        if (value !== undefined && value !== null) {
          const texto = value.toString().trim();
          if (texto !== '') {
            params.append(key, texto);
          }
        }
      });

      const { data: response } = await api.get(`/cotacao/listar_fornecedores_cotacao/${props.idCotacao}?${params.toString()}`);
      const fornecedoresData = response.fornecedores || response || [];
      const scrollInfo = response.scroll || {};
      const nextOffset = scrollInfo.next_offset;
      const backendHasMore = scrollInfo.has_more !== undefined ? scrollInfo.has_more : true;

      if (fornecedoresData.length === 0) {
        hasMore.value = false;
      } else {
        const novosFornecedores = fornecedoresData.map((fornecedor, index) => {
          const baseId = fornecedor.idfornecedor || fornecedor.id || fornecedor.cod_fornecedor || `idx-${index}`;
          const sequencial = (offset.value || 0) + index;
          const itemId = `${props.idCotacao || 'cotacao'}-${baseId}-${sequencial}`;

          let contatosCount = 0;
          if (Array.isArray(fornecedor.representantes)) {
            contatosCount = fornecedor.representantes.filter(rep => {
              const participa = rep.participa;
              return participa === true || participa === 'true' || participa === 1;
            }).length;
          } else {
            contatosCount = fornecedor.contatos || fornecedor.contatos_count || fornecedor.qtd_contatos || 0;
          }

          return {
            ...fornecedor,
            _itemId: itemId,
            fornecedor: fornecedores.value.length + index + 1,
            cod_fornecedor: fornecedor.cod_fornecedor || fornecedor.idfornecedor || fornecedor.id || '',
            contatos: contatosCount,
          };
        });

        if (reset) {
          fornecedores.value = novosFornecedores;
          offset.value = nextOffset !== undefined ? nextOffset : novosFornecedores.length;
          limit.value = offset.value + limitInicial;
          hasMore.value = backendHasMore !== undefined ? backendHasMore : (novosFornecedores.length > 0);
        } else {
          fornecedores.value = [...fornecedores.value, ...novosFornecedores];
          offset.value = nextOffset !== undefined ? nextOffset : fornecedores.value.length;
          limit.value = offset.value + limitInicial;
          hasMore.value = backendHasMore !== undefined ? backendHasMore : (novosFornecedores.length > 0);
        }
      }

      if (reset && props.idCotacao) {
        try {
          const { data: cotacaoResponse } = await api.get(`/cotacao/criar_cotacao_buscar_detalhes_cotacao?cotacao_transacao_id=${props.idCotacao}`);
          cotacaoTitulo.value = (cotacaoResponse && cotacaoResponse.cotacao && cotacaoResponse.cotacao.cotacao_titulo) || '';
        } catch (error) {
          toast.error("Erro ao buscar título da cotação.");
        }
      }
    } catch (error) {
      toast.error("Não foi possível carregar os fornecedores!");
    } finally {
      isLoading.value = false;
      isLoadingMore.value = false;
    }
  };

  const construirFiltrosBusca = () => {
    const novoFiltro = {};
    const termo = searchQuery.value || '';
    const texto = termo.trim();

    if (texto !== '') {
      const apenasDigitos = texto.replace(/\D/g, '');
      if (apenasDigitos && apenasDigitos.length >= 3) {
        novoFiltro.cnpj = apenasDigitos;
      } else {
        novoFiltro.razao_social = texto;
      }
    }

    return novoFiltro;
  };

  const aplicarFiltrosBusca = async () => {
    const novoFiltro = construirFiltrosBusca();
    const filtrosAtuais = JSON.stringify(filtros.value);
    const filtrosNovos = JSON.stringify(novoFiltro);

    filtros.value = novoFiltro;

    if (filtrosAtuais === filtrosNovos) {
      return;
    }

    if (props.visible && props.idCotacao) {
      await getFornecedores(true);
    }
  };

  const excluirFornecedor = async (fornecedor) => {
    if (!props.idCotacao) {
      toast.error("ID da cotação não informado!");
      return false;
    }

    const fornecedorId = fornecedor
      ? (fornecedor.idfornecedor || fornecedor.id || fornecedor.cod_fornecedor || fornecedor._itemId)
      : null;
    if (!fornecedorId) {
      toast.error("ID do fornecedor não encontrado!");
      return false;
    }

    try {
      await api.delete(`/cotacao/excluir_fornecedor_cotacao/${props.idCotacao}/${fornecedorId}`);
      toast.success("Fornecedor removido da cotação!");
      await getFornecedores(true);
      return true;
    } catch (error) {
      toast.error("Não foi possível remover o fornecedor. Tente novamente.");
      return false;
    }
  };

  const excluirSelecionados = async () => {
    if (!props.idCotacao) {
      toast.error("ID da cotação não informado!");
      return false;
    }

    if (!selected.value || selected.value.length === 0) {
      toast.warning("Selecione pelo menos um fornecedor para remover.");
      return false;
    }

    isLimpandoLista.value = true;
    const removidos = [];
    const erros = [];

    try {
      for (const fornecedor of selected.value) {
        const fornecedorId = fornecedor.idfornecedor || fornecedor.id || fornecedor.cod_fornecedor || fornecedor._itemId;

        if (!fornecedorId) {
          erros.push(fornecedor);
          continue;
        }

        try {
          await api.delete(`/cotacao/excluir_fornecedor_cotacao/${props.idCotacao}/${fornecedorId}`);
          removidos.push(fornecedor);
        } catch (error) {
          erros.push(fornecedor);
        }
      }

      if (removidos.length > 0) {
        toast.success(`${removidos.length} fornecedor(es) removido(s) com sucesso!`);
      }

      if (erros.length > 0) {
        toast.warning(`${erros.length} fornecedor(es) não puderam ser removidos.`);
      }

      selected.value = [];
      await getFornecedores(true);
      return removidos.length > 0;
    } catch (error) {
      toast.error("Não foi possível remover los fornecedores selecionados.");
      return false;
    } finally {
      isLimpandoLista.value = false;
    }
  };

  const criarContato = async (fornecedorId, contato, options = {}) => {
    if (!fornecedorId) {
      toast.error("Fornecedor inválido para adicionar contato.");
      return { success: false };
    }

    const payload = {
      email: (contato.email || "").trim(),
      telefone: (contato.telefone || "").trim(),
      nome_representante: (contato.nome_representante || "").trim(),
      cargo_representante: (contato.cargo_representante || "").trim(),
    };

    if (options.alternativo) {
      payload.criar_como_alternativo = true;
    }

    try {
      const { data } = await api.post(`/cotacao/criar_representante_fornecedor/${fornecedorId}`, payload);

      if (data && data.success === false) {
        const mensagemErro = data.error || data.message || "";
        const isDuplicate = mensagemErro.toLowerCase().includes('cadastrado') ||
          mensagemErro.toLowerCase().includes('existe');

        const error = {
          response: {
            status: isDuplicate ? 409 : 400,
            data: data
          }
        };
        throw error;
      }

      toast.success("Contato criado com sucesso!");
      await getFornecedores(true);
      return { success: true, contato: data.representante || data.contato };
    } catch (error) {
      const apiError = error && error.response ? error.response : null;
      let duplicate = apiError && apiError.status === 409;
      const mensagem = apiError && apiError.data && (apiError.data.message || apiError.data.error)
        ? (apiError.data.message || apiError.data.error)
        : "Não foi possível criar o contato.";

      if (!duplicate && mensagem && (mensagem.toLowerCase().includes('cadastrado') || mensagem.toLowerCase().includes('existe'))) {
        duplicate = true;
      }

      if (!duplicate) {
        toast.error(mensagem);
        return { success: false, errorMessage: mensagem };
      }

      const fornecedorNome =
        apiError && apiError.data
          ? (apiError.data.fornecedor_nome ||
            (apiError.data.fornecedor && apiError.data.fornecedor.razao_social) ||
            (apiError.data.fornecedor_existente && apiError.data.fornecedor_existente.razao_social) ||
            '')
          : '';

      const campoDuplicado = apiError && apiError.data && apiError.data.campo_duplicado
        ? apiError.data.campo_duplicado
        : (mensagem && mensagem.toLowerCase().includes('email') ? 'email' :
          mensagem && mensagem.toLowerCase().includes('telefone') ? 'telefone' : null);

      return {
        success: false,
        duplicate: true,
        campoDuplicado: campoDuplicado,
        message: mensagem,
        contatoExistente: apiError && apiError.data && (apiError.data.contato_existente || apiError.data.contato),
        fornecedorNome,
      };
    }
  };

  const atualizarContato = async (contato) => {
    if (!contato || !contato.cotacao_representante_fornecedor_id) {
      toast.error("Contato inválido para atualização.");
      return { success: false, errorMessage: "Contato inválido para atualização." };
    }

    const payload = {
      email: (contato.email || "").trim(),
      telefone: (contato.telefone || "").trim(),
      nome_representante: (contato.nome_representante || "").trim(),
      cargo_representante: (contato.cargo_representante || "").trim(),
    };

    try {
      const { data } = await api.put(
        `/cotacao/atualizar_representante_fornecedor/${contato.cotacao_representante_fornecedor_id}`,
        payload
      );

      if (data && data.success === false) {
        const mensagemErro = data.error || data.message || "";
        const isDuplicate =
          mensagemErro.toLowerCase().includes("cadastrado") ||
          mensagemErro.toLowerCase().includes("existe") ||
          mensagemErro.toLowerCase().includes("já ");
        throw {
          response: {
            status: isDuplicate ? 409 : 400,
            data,
          },
        };
      }

      toast.success("Contato atualizado com sucesso!");
      await getFornecedores(true);
      return { success: true };
    } catch (error) {
      const apiError = error && error.response ? error.response : null;
      let duplicate = apiError && apiError.status === 409;
      const mensagem =
        apiError && apiError.data && (apiError.data.message || apiError.data.error)
          ? apiError.data.message || apiError.data.error
          : "Não foi possível atualizar o contato.";

      if (!duplicate && mensagem && (mensagem.toLowerCase().includes("cadastrado") || mensagem.toLowerCase().includes("existe") || mensagem.toLowerCase().includes("já "))) {
        duplicate = true;
      }

      if (!duplicate) {
        toast.error(mensagem);
        return { success: false, errorMessage: mensagem };
      }

      const fornecedorNome =
        apiError && apiError.data
          ? apiError.data.fornecedor_nome ||
            (apiError.data.fornecedor && apiError.data.fornecedor.razao_social) ||
            (apiError.data.fornecedor_existente && apiError.data.fornecedor_existente.razao_social) ||
            ""
          : "";

      let campoDuplicado =
        apiError && apiError.data && apiError.data.campo_duplicado
          ? apiError.data.campo_duplicado
          : null;
      if (!campoDuplicado) {
        const m = (mensagem || "").toLowerCase();
        if (m.includes("email") || m.includes("e-mail")) campoDuplicado = "email";
        else if (m.includes("telefone") || m.includes("whatsapp")) campoDuplicado = "telefone";
      }

      return {
        success: false,
        duplicate: true,
        campoDuplicado,
        message: mensagem,
        errorMessage: mensagem,
        fornecedorNome,
      };
    }
  };

  const deletarContato = async (contato) => {
    if (!contato || !contato.cotacao_representante_fornecedor_id) {
      toast.error("Contato inválido para exclusão.");
      return false;
    }

    try {
      await api.delete(`/cotacao/deletar_representante_fornecedor/${contato.cotacao_representante_fornecedor_id}`);
      toast.success("Contato excluído com sucesso!");
      await getFornecedores(true);
      return true;
    } catch (error) {
      const apiError = error && error.response ? error.response : null;
      const mensagem = apiError && apiError.data && apiError.data.message
        ? apiError.data.message
        : "Não foi possível excluir o contato.";
      toast.error(mensagem);
      return false;
    }
  };

  const loadMore = async () => {
    if (!isLoadingMore.value && hasMore.value && !isLoading.value) {
      await getFornecedores(false);
    }
  };

  watch(searchQuery, () => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    searchTimeout = setTimeout(() => {
      aplicarFiltrosBusca().catch(() => {
      });
    }, 500);
  });

  watch(() => props.visible,
    async (newVisible) => {
      if (newVisible && props.idCotacao) {
        await getFornecedores(true);
      }
    },
    { immediate: true }
  );

  watch(() => props.idCotacao,
    async (newId) => {
      if (newId && props.visible) {
        await getFornecedores(true);
      }
    }
  );

  const {
    dataTable,
    scrollSentinel,
    onItemsChange,
    onVisibleChange,
    resetScrollPosition
  } = useTableScroll({
    loadMore,
    hasMore,
    isLoadingMore,
    isLoading,
    items: fornecedores
  });

  watch(() => fornecedores.value.length, onItemsChange);
  watch(() => props.visible, onVisibleChange);

  onMounted(() => {
    if (props.visible) {
      setTimeout(() => {
        onVisibleChange(true);
      }, 800);
    }
  });

  onBeforeUnmount(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
  });

  const abrirDialogContatos = (lista, initialIndex = 0, mostrarFiltro = false) => {
    if (!lista || lista.length === 0) {
      toast.warning("Nenhum fornecedor disponível para exibir contatos.");
      return;
    }
    fornecedoresParaContatos.value = lista.map((item) => ({ ...item }));
    indiceFornecedorContato.value = Math.min(Math.max(initialIndex, 0), lista.length - 1);
    mostrarFiltroSemContato.value = mostrarFiltro;
    contatosDialogVisible.value = true;
  };

  const handleCloseContatosDialog = () => {
    contatosDialogVisible.value = false;
  };

  const handleSalvarContatos = async () => {
    contatosDialogVisible.value = false;
    await getFornecedores(true);
  };

  const handleVisualizarContatos = (fornecedor) => {
    if (!fornecedor) return;
    abrirDialogContatos([fornecedor], 0, false);
  };

  const handleRevisarContatos = () => {
    if (!selected.value || selected.value.length === 0) {
      toast.warning("Selecione pelo menos um fornecedor para revisar os contatos.");
      return;
    }
    const lista = selected.value
      .map((item) => fornecedores.value.find((f) => f._itemId === item._itemId) || item)
      .filter(Boolean);
    abrirDialogContatos(lista, 0, true);
  };

  const handleSalvarContatoAtualizado = async ({ contato }, callback) => {
    const resultado = await atualizarContato(contato);
    if (resultado && resultado.success) {
      const fornecedorAtualNoModal = fornecedoresParaContatos.value[indiceFornecedorContato.value];
      if (fornecedorAtualNoModal) {
        const fornecedorAtualizado = fornecedores.value.find(
          (f) => f._itemId === fornecedorAtualNoModal._itemId
        );
        if (fornecedorAtualizado) {
          fornecedoresParaContatos.value = fornecedoresParaContatos.value.map((item) =>
            item._itemId === fornecedorAtualizado._itemId ? { ...fornecedorAtualizado } : item
          );
        }
      }
      resetScrollPosition();
    }
    if (callback) {
      callback(resultado);
    }
  };

  const handleCriarContato = async (payload, callback) => {
    if (!payload || !payload.fornecedorId || !payload.contato) {
      if (callback) callback({ success: false, errorMessage: "Dados inválidos." });
      return;
    }

    const resultado = await criarContato(payload.fornecedorId, payload.contato, payload.options || {});

    if (resultado.success) {
      const fornecedorAtualNoModal = fornecedoresParaContatos.value[indiceFornecedorContato.value];
      if (fornecedorAtualNoModal) {
        const fornecedorAtualizado = fornecedores.value.find(
          (f) =>
            f._itemId === fornecedorAtualNoModal._itemId ||
            (f.idfornecedor || f.id) === payload.fornecedorId
        );
        if (fornecedorAtualizado) {
          fornecedoresParaContatos.value = fornecedoresParaContatos.value.map((item) =>
            item._itemId === fornecedorAtualizado._itemId ? { ...fornecedorAtualizado } : item
          );
        }
      }
      resetScrollPosition();
    }

    if (callback) {
      callback(resultado);
    }
  };

  const handleExcluirContato = async ({ contato }) => {
    const sucesso = await deletarContato(contato);
    if (sucesso) {
      const fornecedorAtualNoModal = fornecedoresParaContatos.value[indiceFornecedorContato.value];
      if (fornecedorAtualNoModal) {
        const fornecedorAtualizado = fornecedores.value.find(
          (f) => f._itemId === fornecedorAtualNoModal._itemId
        );
        if (fornecedorAtualizado) {
          fornecedoresParaContatos.value = fornecedoresParaContatos.value.map((item) =>
            item._itemId === fornecedorAtualizado._itemId ? { ...fornecedorAtualizado } : item
          );
        }
      }
      resetScrollPosition();
    }
  };

  // ============ SUPPLIER ADDITION HANDLERS ============
  const openAdicionarFornecedores = ref(false);

  const handleAbrirAdicionarFornecedores = () => {
    openAdicionarFornecedores.value = true;
  };

  const handleFecharAdicionarFornecedores = async () => {
    if (props.idCotacao) {
      openAdicionarFornecedores.value = false;
      await nextTick();
      await new Promise(resolve => setTimeout(resolve, 200));

      try {
        await getFornecedores(true);
        resetScrollPosition();
      } catch (error) {
        toast.error("Não foi possível recarregar os fornecedores.");
      }
    } else {
      openAdicionarFornecedores.value = false;
    }
  };

  const handleFornecedoresAdicionados = async () => {
    if (props.idCotacao) {
      await getFornecedores(true);
      resetScrollPosition();
    }
  };

  // ============ DELETE HANDLERS ============
  const handleExcluir = async (item) => {
    const sucesso = await excluirFornecedor(item);
    if (sucesso) {
      resetScrollPosition();
    }
  };

  const excluirTodosFornecedores = async () => {
    if (!props.idCotacao) {
      toast.error("ID da cotação não informado!");
      return false;
    }

    if (!fornecedores.value || fornecedores.value.length === 0) {
      toast.warning("Não há fornecedores para remover.");
      return false;
    }

    isLimpandoLista.value = true;
    const removidos = [];
    const erros = [];

    try {
      // Excluir todos os fornecedores da lista
      for (const fornecedor of fornecedores.value) {
        const fornecedorId = fornecedor.idfornecedor || fornecedor.id || fornecedor.cod_fornecedor || fornecedor._itemId;

        if (!fornecedorId) {
          erros.push(fornecedor);
          continue;
        }

        try {
          await api.delete(`/cotacao/excluir_fornecedor_cotacao/${props.idCotacao}/${fornecedorId}`);
          removidos.push(fornecedor);
        } catch (error) {
          erros.push(fornecedor);
        }
      }

      if (removidos.length > 0) {
        toast.success(`${removidos.length} fornecedor(es) removido(s) com sucesso!`);
      }

      if (erros.length > 0) {
        toast.warning(`${erros.length} fornecedor(es) não puderam ser removidos.`);
      }

      selected.value = [];
      await getFornecedores(true);
      return removidos.length > 0;
    } catch (error) {
      toast.error("Não foi possível remover los fornecedores.");
      return false;
    } finally {
      isLimpandoLista.value = false;
    }
  };

  const handleLimparLista = async () => {
    let sucesso = false;

    // Se há itens selecionados, excluir apenas os selecionados
    if (selected.value && selected.value.length > 0) {
      sucesso = await excluirSelecionados();
    } else {
      // Se não há itens selecionados, excluir todos os fornecedores
      sucesso = await excluirTodosFornecedores();
    }

    if (sucesso) {
      resetScrollPosition();
    }
  };

  return {
    isLoading,
    isLoadingMore,
    fornecedores,
    selected,
    headers,
    alturaTabela,
    cotacaoTitulo,
    hasMore,
    getFornecedores,
    loadMore,
    searchQuery,
    fornecedoresFiltrados,
    formatCNPJ,
    isLimpandoLista,
    excluirFornecedor,
    excluirSelecionados,
    criarContato,
    atualizarContato,
    deletarContato,
    // Scroll handling
    dataTable,
    scrollSentinel,
    resetScrollPosition,
    // Contact dialog
    contatosDialogVisible,
    fornecedoresParaContatos,
    indiceFornecedorContato,
    mostrarFiltroSemContato,
    handleCloseContatosDialog,
    handleSalvarContatos,
    handleVisualizarContatos,
    handleRevisarContatos,
    handleSalvarContatoAtualizado,
    handleCriarContato,
    handleExcluirContato,
    // Supplier addition
    openAdicionarFornecedores,
    handleAbrirAdicionarFornecedores,
    handleFecharAdicionarFornecedores,
    handleFornecedoresAdicionados,
    // Delete handlers
    handleExcluir,
    handleLimparLista,
  };
}
